import React from 'react';
import { FileText, TrendingUp, AlertCircle, CheckCircle, Info, Target } from 'react-feather';

const CommentsResume = ({ resume }) => {
  if (!resume) return null;

  // Handle both old string format and new object format
  const isString = typeof resume === 'string';
  const resumeData = isString ? { summary: resume } : resume;

  const getInsightIcon = (type) => {
    switch (type) {
      case 'opportunity':
        return <Target size={14} className="text-blue-600" />;
      case 'warning':
        return <AlertCircle size={14} className="text-orange-600" />;
      case 'success':
        return <CheckCircle size={14} className="text-green-600" />;
      default:
        return <Info size={14} className="text-gray-600" />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'very positive':
      case 'positive':
        return 'from-green-50 to-emerald-50 border-green-200';
      case 'concerned':
        return 'from-orange-50 to-red-50 border-orange-200';
      case 'mixed':
        return 'from-yellow-50 to-amber-50 border-yellow-200';
      case 'engaged':
        return 'from-blue-50 to-cyan-50 border-blue-200';
      default:
        return 'from-purple-50 to-blue-50 border-purple-200';
    }
  };

  return (
    <div className={`bg-gradient-to-r ${getSentimentColor(resumeData.sentiment)} border-2 rounded-xl p-5 sm:p-6 mb-6 shadow-sm`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <FileText size={22} className="text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Comments Analysis</h3>
            {resumeData.sentiment && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                resumeData.sentiment === 'very positive' || resumeData.sentiment === 'positive'
                  ? 'bg-green-100 text-green-800'
                  : resumeData.sentiment === 'concerned'
                  ? 'bg-orange-100 text-orange-800'
                  : resumeData.sentiment === 'engaged'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {resumeData.sentimentIntensity ? `${resumeData.sentiment} (${resumeData.sentimentIntensity})` : resumeData.sentiment}
              </span>
            )}
          </div>
          
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 break-words overflow-wrap-anywhere">
            {resumeData.summary}
          </p>

          {/* Insights Section */}
          {resumeData.insights && resumeData.insights.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-purple-600" />
                Key Insights
              </h4>
              <div className="space-y-2">
                {resumeData.insights.map((insight, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 p-2 rounded-lg ${
                      insight.type === 'opportunity'
                        ? 'bg-blue-50 border border-blue-100'
                        : insight.type === 'warning'
                        ? 'bg-orange-50 border border-orange-100'
                        : insight.type === 'success'
                        ? 'bg-green-50 border border-green-100'
                        : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    {getInsightIcon(insight.type)}
                    <p className="text-xs sm:text-sm text-gray-700 flex-1 break-words overflow-wrap-anywhere">
                      {insight.text}
                      {insight.intensity && (
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                          insight.intensity === 'high'
                            ? 'bg-red-100 text-red-700'
                            : insight.intensity === 'moderate'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {insight.intensity}
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metrics Section */}
          {resumeData.metrics && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Metrics Breakdown</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div className="bg-white bg-opacity-60 rounded p-2">
                  <div className="text-gray-600">Total</div>
                  <div className="font-bold text-gray-900">{resumeData.metrics.total}</div>
                </div>
                {resumeData.metrics.buyerPct > 0 && (
                  <div className="bg-blue-50 rounded p-2">
                    <div className="text-blue-700">Purchase Intent</div>
                    <div className="font-bold text-blue-900">{resumeData.metrics.buyerPct}%</div>
                  </div>
                )}
                {resumeData.metrics.frictionPct > 0 && (
                  <div className="bg-orange-50 rounded p-2">
                    <div className="text-orange-700">Friction</div>
                    <div className="font-bold text-orange-900">{resumeData.metrics.frictionPct}%</div>
                  </div>
                )}
                {resumeData.metrics.positivePct > 0 && (
                  <div className="bg-green-50 rounded p-2">
                    <div className="text-green-700">Positive</div>
                    <div className="font-bold text-green-900">{resumeData.metrics.positivePct}%</div>
                  </div>
                )}
                {resumeData.metrics.questionPct > 0 && (
                  <div className="bg-purple-50 rounded p-2">
                    <div className="text-purple-700">Questions</div>
                    <div className="font-bold text-purple-900">{resumeData.metrics.questionPct}%</div>
                  </div>
                )}
                {resumeData.metrics.suggestionPct > 0 && (
                  <div className="bg-cyan-50 rounded p-2">
                    <div className="text-cyan-700">Suggestions</div>
                    <div className="font-bold text-cyan-900">{resumeData.metrics.suggestionPct}%</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentsResume;

